"use client";

import React, { useEffect, useRef, useState, useCallback } from "react";
import { Search, SlidersHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";
import "./PharmacyHeader.css";

export default function PharmacyHeader({
  searchQuery,
  setSearchQuery,
  activeTab,
  setActiveTab,
  showTabs = true,
  className = "",
  title = "Pharmacy Locator",
  subtitle = "Find the nearest pharmacy to you.",
  enableFilters = true,
}) {
  const [showOptions, setShowOptions] = useState(true);
  const [openField, setOpenField] = useState("none");
  const searchInputRef = useRef(null);
  const headerRef = useRef(null);
	  const { t } = useTranslation();

  const toggleSearch = useCallback(() => {
    setOpenField((prev) => {
      const next = prev === "search" ? "none" : "search";
      // focus léger différé
      setTimeout(() => next === "search" && searchInputRef.current?.focus(), 10);
      return next;
    });
  }, []);

  // Fermer si clic en dehors / ESC
  useEffect(() => {
    const onDown = (e) => {
      if (!headerRef.current) return;
      if (!headerRef.current.contains(e.target)) setOpenField("none");
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpenField("none");
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // Handlers mémoïsés
  const handleSearchChange = useCallback(
    (e) => setSearchQuery(e.target.value),
    [setSearchQuery]
  );

  const handleTabClick = useCallback(
    (tab) => () => setActiveTab(tab),
    [setActiveTab]
  );

  const handleSearchPillClick = useCallback(
    (e) => {
      // si on clique dans l'input, ne pas fermer/ouvrir
      if (e.target.tagName !== "INPUT") toggleSearch();
    },
    [toggleSearch]
  );

  return (
    <>
      <header className={`phl-header ${className}`} ref={headerRef}>
        <div className="phl-left">
          <h2 className="phl-title">{title}</h2>
          {subtitle ? <p className="phl-subtitle">{subtitle}</p> : null}
        </div>

        <div className="phl-right">
          {/* ---- Search pill ---- */}
          <div
            className={`phl-pill phl-search ${openField === "search" ? "is-open" : "is-closed"}`}
            onClick={handleSearchPillClick}
            role="group"
	            aria-label={t("locators.common.searchAriaGroup")}
          >
            <button
              type="button"
              className="phl-pill-ico"
	              aria-label={t("locators.common.searchAriaOpen")}
              onClick={(e) => {
                e.stopPropagation();
                toggleSearch();
              }}
            >
              <Search className="phl-ico" aria-hidden />
            </button>

            <input
              ref={searchInputRef}
              type="search"
	              aria-label={t("locators.common.searchInputAria")}
              autoComplete="on"
	              placeholder={t("locators.common.searchPlaceholder")}
              value={searchQuery}
              onChange={handleSearchChange}
              className="phl-input"
              onKeyDown={(e) => {
                if (e.key === "Enter") e.preventDefault(); // le parent fait le fetch
              }}
              onFocus={() => setOpenField("search")}
            />
          </div>

          {/* bouton pour afficher/masquer la barre d’onglets */}
          {enableFilters && (
            <button
              className={`phl-iconbtn ${showOptions ? "is-active" : ""}`}
              type="button"
	              aria-label={t("locators.common.toggleFiltersAria")}
              aria-pressed={showOptions}
              onClick={() => setShowOptions((v) => !v)}
            >
              <SlidersHorizontal className="phl-ico" />
            </button>
          )}
        </div>
      </header>

      {/* Barre d’onglets */}
      {enableFilters && showTabs && (
        <div className={`phl-tabsbar ${showOptions ? "open" : ""}`}>
	          <div className="phl-tabs" role="tablist" aria-label={t("locators.common.tablistAria")}>
            <button
              role="tab"
              aria-selected={activeTab === "Pharmacy"}
              className={activeTab === "Pharmacy" ? "active" : ""}
              onClick={handleTabClick("Pharmacy")}
            >
	              {t("locators.header.tabs.pharmacy")}
            </button>
            <button
              role="tab"
              aria-selected={activeTab === "Parapharmacy"}
              className={activeTab === "Parapharmacy" ? "active" : ""}
              onClick={handleTabClick("Parapharmacy")}
            >
	              {t("locators.header.tabs.parapharmacy")}
            </button>
            <button
              role="tab"
              aria-selected={activeTab === "OnCall"}
              className={activeTab === "OnCall" ? "active" : ""}
              onClick={handleTabClick("OnCall")}
            >
	              {t("locators.header.tabs.onCall")}
            </button>
            <button
              role="tab"
              aria-selected={activeTab === "Night"}
              className={activeTab === "Night" ? "active" : ""}
              onClick={handleTabClick("Night")}
            >
	              {t("locators.header.tabs.night")}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
